/**
 * EnviosAdmin — administración operativa de envío (MVP-02C).
 *
 * Hace operable desde React lo que antes exigía Django Admin: origen,
 * embalajes, tarifas, calendario y una prueba de cotización en seco, todo
 * sobre el dominio real de `store/shipping/` (ninguna fórmula se reimplementa
 * aquí). El diagnóstico de arriba explica exactamente qué falta y por qué el
 * checkout físico puede seguir bloqueado; el digital nunca depende de esto.
 */
import { useCallback, useEffect, useState } from "react";
import {
    AlertTriangle, CalendarClock, CheckCircle2, FlaskConical,
    MapPin, Package, RotateCw, Truck,
} from "lucide-react";

import { fetchReadiness } from "../../../../domain/shipping-admin/api.js";
import EstadoSection from "./sections/EstadoSection.jsx";
import DireccionesSection from "./sections/DireccionesSection.jsx";
import EmbalajesSection from "./sections/EmbalajesSection.jsx";
import TarifasSection from "./sections/TarifasSection.jsx";
import CalendarioSection from "./sections/CalendarioSection.jsx";
import SimuladorSection from "./sections/SimuladorSection.jsx";

const TABS = [
    { id: "estado", label: "Estado", icon: CheckCircle2 },
    { id: "direcciones", label: "Direcciones", icon: MapPin },
    { id: "embalajes", label: "Embalajes", icon: Package },
    { id: "tarifas", label: "Tarifas", icon: Truck },
    { id: "calendario", label: "Calendario y plazos", icon: CalendarClock },
    { id: "simulador", label: "Probar cotización", icon: FlaskConical },
];

const OVERALL_LABEL = {
    sin_configurar: "Sin configurar",
    configuracion_incompleta: "Configuración incompleta",
    listo_para_cotizar: "Listo para cotizar",
};

const OVERALL_STYLE = {
    sin_configurar: "badge-error",
    configuracion_incompleta: "badge-warning",
    listo_para_cotizar: "badge-success",
};

export default function EnviosAdmin() {
    const [activeTab, setActiveTab] = useState("estado");
    const [readiness, setReadiness] = useState({ state: "loading", data: null });

    const reloadReadiness = useCallback(() => {
        setReadiness((prev) => ({ ...prev, state: "loading" }));
        return fetchReadiness()
            .then((data) => setReadiness({ state: "ready", data }))
            .catch(() => setReadiness({ state: "error", data: null }));
    }, []);

    useEffect(() => {
        reloadReadiness();
    }, [reloadReadiness]);

    const overall = readiness.data?.overall;

    return (
        <div className="p-4 lg:p-6 flex flex-col gap-4 h-full overflow-auto">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Truck className="w-7 h-7 text-primary shrink-0" />
                    <div>
                        <h1 className="text-xl font-bold">Envíos</h1>
                        <p className="text-sm text-base-content/60">
                            Origen, embalajes, tarifas y calendario reales que necesita el
                            cotizador de checkout. El pedido digital no depende de esto.
                        </p>
                    </div>
                </div>
                {readiness.state === "ready" && (
                    <span className={`badge ${OVERALL_STYLE[overall] || "badge-ghost"} badge-lg`}>
                        {OVERALL_LABEL[overall] || overall}
                    </span>
                )}
            </header>

            {readiness.state === "error" && (
                <div role="alert" className="alert alert-error text-sm flex-wrap">
                    <AlertTriangle className="w-4 h-4" />
                    <span>No se pudo cargar el diagnóstico de envío.</span>
                    <button type="button" onClick={reloadReadiness} className="btn btn-xs gap-1">
                        <RotateCw className="w-3 h-3" /> Reintentar
                    </button>
                </div>
            )}

            {overall && overall !== "listo_para_cotizar" && (
                <div role="alert" className="alert alert-warning text-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>
                        El checkout físico y mixto seguirán bloqueados hasta completar la
                        configuración. El checkout digital no se ve afectado.
                    </span>
                </div>
            )}

            <nav
                role="tablist"
                aria-label="Secciones de administración de envío"
                className="tabs tabs-boxed flex-wrap"
            >
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === id}
                        className={`tab gap-1.5 ${activeTab === id ? "tab-active" : ""}`}
                        onClick={() => setActiveTab(id)}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
            </nav>

            <div className="flex-1 min-h-0">
                {activeTab === "estado" && (
                    <EstadoSection
                        readiness={readiness}
                        onNavigate={setActiveTab}
                        onRetry={reloadReadiness}
                    />
                )}
                {activeTab === "direcciones" && (
                    <DireccionesSection onChanged={reloadReadiness} />
                )}
                {activeTab === "embalajes" && (
                    <EmbalajesSection onChanged={reloadReadiness} />
                )}
                {activeTab === "tarifas" && (
                    <TarifasSection onChanged={reloadReadiness} />
                )}
                {activeTab === "calendario" && (
                    <CalendarioSection onChanged={reloadReadiness} />
                )}
                {activeTab === "simulador" && <SimuladorSection />}
            </div>
        </div>
    );
}
